// Owns the DOM nodes the launcher injects: a floating button + a wrapper
// containing the panel iframe. All styles inlined (and `!important`, see
// ./style) so the host page's CSS can't corrupt the layout.

import { LAUNCHER_TOKENS } from '@/theme/tokens';
import type {
  RequestLocale,
  ThemeName,
  WidgetMargin,
  WidgetMode,
  WidgetPosition,
} from '@/domain';

import { LAUNCHER_ICONS } from './icons';
import { setImportant } from './style';
import { readableTextColor } from './color';
import {
  attachResizeHandle,
  clampSize,
  loadStoredSize,
  positionHandle,
  saveStoredSize,
  setHandleVisible,
  type PopupSize,
} from './resizable';

const ROOT_ID = 'ba-assistant-root';
const BUTTON_ID = 'ba-assistant-button';
const FRAME_WRAP_ID = 'ba-assistant-frame-wrap';
const FRAME_ID = 'ba-assistant-iframe';
const RESIZE_HANDLE_ID = 'ba-assistant-resize-handle';
/** Stack above host content; set on each fixed element, never on the root. */
const WIDGET_Z_INDEX = '2147483600';

/** Desktop popup default. 456 = the original 380 + 20% (wider popup). */
const POPUP_DEFAULT_W = 456;
const POPUP_DEFAULT_H = 600;
/** Default gap (px) from the docked corner for both launcher + popup. */
const DEFAULT_MARGIN = 20;
/** Default launcher button diameter (px). */
const DEFAULT_LAUNCHER_SIZE = 56;
/** Vertical gap (px) between the launcher button and the popup above it. */
const BUTTON_POPUP_GAP = 16;
/** Image-icon size as a fraction of the button diameter. */
const ICON_SCALE = 0.55;
/** A launcher icon value that is an image URL rather than raw SVG markup. */
const URL_LIKE = /^(https?:|data:|\/|\.\/)/i;

/**
 * FEATURE-120 — accessible names for the injected iframe + launcher button.
 * Brand-neutral on purpose: the launcher never sees tenant config, so naming a
 * brand here ("Booking1A Assistant") leaked Booking1A onto every tenant's site.
 */
export interface LauncherLabels {
  /** `iframe.title` — accessible name of the panel frame. */
  frameTitle: string;
  /** `aria-label` of the floating launcher button. */
  openButton: string;
}

const LABELS_VI: LauncherLabels = { frameTitle: 'Trợ lý AI', openButton: 'Mở trợ lý AI' };
const LABELS_EN: LauncherLabels = { frameTitle: 'AI assistant', openButton: 'Open AI assistant' };

/**
 * The label language follows the host's init locale the way the panel picks
 * its UI language: only an explicit `'en'` is English; `'vi'`, `'auto'` and an
 * omitted locale all render the widget's default UI language (vi).
 */
export function launcherLabels(locale: RequestLocale | undefined): LauncherLabels {
  return locale === 'en' ? LABELS_EN : LABELS_VI;
}

/**
 * FEATURE-35 — viewport threshold below which the panel uses a near-
 * fullscreen layout (popup mode) and the launcher's open() auto-elevates
 * to actual fullscreen via lifecycle.
 */
export const MOBILE_BREAKPOINT_PX = 480;

export function isMobileViewport(win: Window = window): boolean {
  return win.innerWidth < MOBILE_BREAKPOINT_PX;
}

/** Resolved placement + sizing for the launcher button and popup. */
interface LauncherLayout {
  position: WidgetPosition;
  marginX: number;
  marginY: number;
  launcherSize: number;
  defaultW: number;
  defaultH: number;
}

/** The popup sits `marginY + launcherSize + gap` above the viewport bottom. */
function popupBottomGap(layout: LauncherLayout): number {
  return layout.marginY + layout.launcherSize + BUTTON_POPUP_GAP;
}

function resolveLayout(opts: InjectorOptions): LauncherLayout {
  const margin: WidgetMargin = opts.margin ?? {};
  return {
    position: opts.position ?? 'bottom-left',
    marginX: margin.x ?? DEFAULT_MARGIN,
    marginY: margin.y ?? DEFAULT_MARGIN,
    launcherSize: opts.launcherSize ?? DEFAULT_LAUNCHER_SIZE,
    defaultW: opts.width ?? POPUP_DEFAULT_W,
    defaultH: opts.height ?? POPUP_DEFAULT_H,
  };
}

export interface DomHandles {
  root: HTMLElement;
  button: HTMLButtonElement;
  frameWrap: HTMLDivElement;
  iframe: HTMLIFrameElement;
  /** Drag-to-resize grip, shown only in desktop popup mode (when enabled). */
  resizeHandle: HTMLElement;
  /** Resolved placement + sizing (read by applyMode / responsive reflow). */
  layout: LauncherLayout;
  /** Whether the popup may be drag-resized. */
  resizable: boolean;
  /** Whether the floating button is suppressed (host drives open() itself). */
  hideLauncher: boolean;
  /** Custom launcher icon (URL or raw SVG), preserved across theme changes. */
  icon?: string;
  /** Host accent color, preserved across theme changes. */
  accentColor?: string;
}

export interface InjectorOptions {
  panelUrl: string;
  /** FEATURE-34 — first-paint theme. Forwarded to the iframe via `?theme=`. */
  theme: ThemeName;
  /**
   * FEATURE-88 — multi-tenant client code. Forwarded to the iframe via
   * `?clientCode=` so the panel reads it synchronously on mount (without
   * waiting for the `init` postMessage). Omitted entirely when undefined.
   */
  clientCode?: string;
  /**
   * FEATURE-120 — host init locale. Only picks the language of the
   * brand-neutral iframe title / button label (see `launcherLabels`).
   */
  locale?: RequestLocale;
  /** Dock corner for the launcher button + popup. Defaults to 'bottom-left'. */
  position?: WidgetPosition;
  /** Gap (px) from the docked corner. Defaults to { x: 20, y: 20 }. */
  margin?: WidgetMargin;
  /** Desktop popup default width (px). Defaults to 456. */
  width?: number;
  /** Desktop popup default height (px). Defaults to 600. */
  height?: number;
  /** Allow drag-resize of the desktop popup. Defaults to true. */
  resizable?: boolean;
  /** Custom launcher icon — image URL or raw `<svg>` markup. */
  icon?: string;
  /** Launcher button diameter (px). Defaults to 56. */
  launcherSize?: number;
  /** Suppress the floating button (host opens via BAAssistant.open()). */
  hideLauncher?: boolean;
  /** Brand/accent color (any CSS color) for the button + panel highlights. */
  accentColor?: string;
  onLauncherClick: () => void;
}

/**
 * Build the panel iframe URL with theme + optional client code + accent as
 * query params so the panel's bootstrap applies them BEFORE React mounts.
 * Preserves any existing query/hash on the base panelUrl.
 */
export function buildPanelUrl(
  base: string,
  theme: ThemeName,
  clientCode?: string,
  accentColor?: string,
): string {
  try {
    const url = new URL(base, window.location.href);
    url.searchParams.set('theme', theme);
    if (clientCode !== undefined && clientCode.length > 0) {
      url.searchParams.set('clientCode', clientCode);
    }
    if (accentColor !== undefined && accentColor.length > 0) {
      url.searchParams.set('accent', accentColor);
    }
    return url.toString();
  } catch {
    // Fallback for environments where URL parsing fails on the base.
    const sep = base.includes('?') ? '&' : '?';
    const parts = [`theme=${encodeURIComponent(theme)}`];
    if (clientCode !== undefined && clientCode.length > 0) {
      parts.push(`clientCode=${encodeURIComponent(clientCode)}`);
    }
    if (accentColor !== undefined && accentColor.length > 0) {
      parts.push(`accent=${encodeURIComponent(accentColor)}`);
    }
    return `${base}${sep}${parts.join('&')}`;
  }
}

function paintButton(
  button: HTMLButtonElement,
  theme: ThemeName,
  accentColor?: string,
  doc: Document = document,
): void {
  const t = LAUNCHER_TOKENS[theme];
  // Accent overrides the theme background; flip the icon color for contrast
  // so a light accent doesn't render a white icon on a pale circle.
  const background = accentColor ?? t.background;
  const foreground =
    accentColor !== undefined ? (readableTextColor(accentColor, doc) ?? t.foreground) : t.foreground;
  setImportant(button, {
    background,
    color: foreground,
    borderRadius: t.radius,
    boxShadow: t.shadow,
  });
}

/** Render the launcher icon: custom image URL, custom raw SVG, or theme icon. */
function setButtonIcon(
  button: HTMLButtonElement,
  theme: ThemeName,
  icon: string | undefined,
  launcherSize: number,
): void {
  if (icon !== undefined && icon.trim().length > 0) {
    if (URL_LIKE.test(icon.trim())) {
      const inner = Math.round(launcherSize * ICON_SCALE);
      button.innerHTML = '';
      const img = button.ownerDocument.createElement('img');
      img.src = icon;
      img.alt = '';
      setImportant(img, {
        width: `${inner}px`,
        height: `${inner}px`,
        'object-fit': 'contain',
        display: 'block',
      });
      button.appendChild(img);
      return;
    }
    // Raw inline SVG/HTML.
    button.innerHTML = icon;
    return;
  }
  button.innerHTML = LAUNCHER_ICONS[theme];
}

export function injectWidget(opts: InjectorOptions, doc: Document = document): DomHandles {
  const existing = doc.getElementById(ROOT_ID);
  if (existing !== null) existing.remove();

  const layout = resolveLayout(opts);
  const resizable = opts.resizable ?? true;
  const hideLauncher = opts.hideLauncher ?? false;
  const win = doc.defaultView ?? window;

  const root = doc.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('data-ba-assistant', '');
  // The root is a plain container: NO inline position/z-index. Ad-blockers
  // (AdLock) hide `body>div[style*="fixed"][style*="z-index"]`, which used to
  // match this element and took the launcher + panel down with it. The two
  // fixed children carry WIDGET_Z_INDEX themselves instead.
  // It must still be neutral against host CSS such as `body>div{position:relative;
  // z-index:1}` (caps the children's stacking under the page) or
  // `body>div{transform:...}` (makes the root the containing block, so the
  // "fixed" children scroll away with the page). z-index must be reset too: on a
  // flex/grid body a static item with z-index still opens a stacking context.
  // No value here contains "fixed", so the ad-blocker rule (which needs both
  // "fixed" and "z-index") still does not match.
  setImportant(root, {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    position: 'static',
    zIndex: 'auto',
    transform: 'none',
    filter: 'none',
    perspective: 'none',
    contain: 'none',
    willChange: 'auto',
  });

  const frameWrap = doc.createElement('div');
  frameWrap.id = FRAME_WRAP_ID;
  setImportant(frameWrap, {
    position: 'fixed',
    zIndex: WIDGET_Z_INDEX,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#fff',
    display: 'none',
    transition: 'all 200ms ease',
  });
  applyPopupDimensions(frameWrap, win, layout);

  const iframe = doc.createElement('iframe');
  iframe.id = FRAME_ID;
  // FEATURE-120 — brand-neutral accessible names (see `launcherLabels`).
  const labels = launcherLabels(opts.locale);
  iframe.title = labels.frameTitle;
  iframe.src = buildPanelUrl(opts.panelUrl, opts.theme, opts.clientCode, opts.accentColor);
  iframe.setAttribute('allow', 'microphone');
  iframe.dataset.theme = opts.theme;
  setImportant(iframe, {
    width: '100%',
    height: '100%',
    border: '0',
    display: 'block',
  });
  frameWrap.appendChild(iframe);

  const button = doc.createElement('button');
  button.id = BUTTON_ID;
  button.type = 'button';
  button.setAttribute('aria-label', labels.openButton);
  button.dataset.theme = opts.theme;
  setButtonIcon(button, opts.theme, opts.icon, layout.launcherSize);
  setImportant(button, {
    position: 'fixed',
    zIndex: WIDGET_Z_INDEX,
    bottom: `${layout.marginY}px`,
    width: `${layout.launcherSize}px`,
    height: `${layout.launcherSize}px`,
    border: '0',
    // Hidden when the host drives the widget from its own UI.
    display: hideLauncher ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 150ms ease',
    // Dock to the configured corner so the button clears host sidebars.
    ...(layout.position === 'bottom-right'
      ? { right: `${layout.marginX}px`, left: 'auto' }
      : { left: `${layout.marginX}px`, right: 'auto' }),
  });
  paintButton(button, opts.theme, opts.accentColor, doc);
  button.addEventListener('mouseenter', () => {
    button.style.transform = 'scale(1.04)';
  });
  button.addEventListener('mouseleave', () => {
    button.style.transform = '';
  });
  button.addEventListener('click', opts.onLauncherClick);

  // Drag-to-resize grip (when enabled). Lives inside frameWrap (above the
  // iframe). The closure captures `layout` for the latest dock corner;
  // lifecycle never changes placement after init, so that's correct.
  const resizeHandle = resizable
    ? attachResizeHandle(
        {
          frameWrap,
          iframe,
          getPosition: () => layout.position,
          getMode: () =>
            frameWrap.style.getPropertyValue('width') === '100vw' ? 'fullscreen' : 'popup',
          applySize: (size) => {
            applyPopupSize(frameWrap, size);
            saveStoredSize(size, win);
          },
        },
        doc,
      )
    : createInertHandle(frameWrap, doc);
  setHandleVisible(resizeHandle, resizable && !isMobileViewport(win));

  root.appendChild(button);
  root.appendChild(frameWrap);
  doc.body.appendChild(root);

  return {
    root,
    button,
    frameWrap,
    iframe,
    resizeHandle,
    layout,
    resizable,
    hideLauncher,
    icon: opts.icon,
    accentColor: opts.accentColor,
  };
}

/** A non-interactive, always-hidden stand-in when resizing is disabled. */
function createInertHandle(frameWrap: HTMLElement, doc: Document): HTMLElement {
  const el = doc.createElement('div');
  el.id = RESIZE_HANDLE_ID;
  el.setAttribute('aria-hidden', 'true');
  setImportant(el, { display: 'none' });
  frameWrap.appendChild(el);
  return el;
}

/** Apply a concrete pixel size to the popup (used by drag-resize + restore). */
function applyPopupSize(wrap: HTMLElement, size: PopupSize): void {
  setImportant(wrap, { width: `${size.width}px`, height: `${size.height}px` });
}

export function setVisible(handles: DomHandles, visible: boolean): void {
  setImportant(handles.frameWrap, { display: visible ? 'block' : 'none' });
}

export function applyMode(handles: DomHandles, mode: WidgetMode): void {
  const wrap = handles.frameWrap;
  if (mode === 'fullscreen') {
    // Anchor from top-left + viewport-unit size only. Do NOT also pin
    // right/bottom to 0 — that over-constrains the fixed box (left+right+
    // width all set), and some host layouts resolve the conflict by honouring
    // right/bottom, shifting the overlay instead of covering the viewport.
    // `right/bottom: auto` keeps it a clean full-bleed box.
    //
    // That alone is NOT enough on real AdminLTE/Bootstrap hosts: their global
    // `!important` positioning rules still beat our plain inline `top/left`
    // and shifted the overlay ~90px right / 124px up. So apply with
    // `!important` (see setImportant) — that is what actually pins it.
    setImportant(wrap, {
      left: '0',
      top: '0',
      right: 'auto',
      bottom: 'auto',
      width: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      borderRadius: '0',
    });
    setImportant(handles.button, { display: 'none' });
    // No resizing in fullscreen — it already fills the viewport.
    setHandleVisible(handles.resizeHandle, false);
  } else {
    applyPopupDimensions(wrap, window, handles.layout);
    // Respect hideLauncher — the host may drive open() from its own UI.
    setImportant(handles.button, { display: handles.hideLauncher ? 'none' : 'flex' });
    // Resize grip only on desktop popups (mobile is full-bleed), when enabled.
    setHandleVisible(handles.resizeHandle, handles.resizable && !isMobileViewport(window));
  }
}

/**
 * FEATURE-35 — popup-mode dimensions. Desktop: host-configurable size
 * (default 456×600) docked to the configured corner with the configured
 * margins; a user-resized size is restored from localStorage when present.
 * Mobile (< MOBILE_BREAKPOINT_PX): full-bleed minus 8px gutters,
 * `min(75vh, 600px)` tall, 12px from bottom — not resizable.
 */
function applyPopupDimensions(
  wrap: HTMLDivElement,
  win: Window,
  layout: LauncherLayout,
): void {
  if (win.innerWidth < MOBILE_BREAKPOINT_PX) {
    setImportant(wrap, {
      left: '8px',
      right: '8px',
      bottom: '12px',
      top: 'auto',
      width: 'auto',
      height: 'min(75vh, 600px)',
      maxHeight: 'calc(100vh - 24px)',
      borderRadius: '16px',
    });
    return;
  }
  const bottomGap = popupBottomGap(layout);
  // Restore a user-resized size (clamped to the current viewport), else default.
  const stored = loadStoredSize(win);
  const size =
    stored !== null
      ? clampSize(stored.width, stored.height, win)
      : { width: layout.defaultW, height: layout.defaultH };
  setImportant(wrap, {
    bottom: `${bottomGap}px`,
    top: 'auto',
    width: `${size.width}px`,
    height: `${size.height}px`,
    // Keep the box within the viewport even if the stored size is stale.
    maxHeight: `calc(100vh - ${bottomGap + 12}px)`,
    borderRadius: '16px',
    // Anchor to the docked side; the opposite edge is free to grow on resize.
    ...(layout.position === 'bottom-right'
      ? { right: `${layout.marginX}px`, left: 'auto' }
      : { left: `${layout.marginX}px`, right: 'auto' }),
  });
}

/**
 * FEATURE-35 — re-apply responsive dimensions when the host window
 * resizes (e.g. mobile orientation change). Lifecycle wires the
 * window-level listener and calls this.
 */
export function applyResponsiveLayout(handles: DomHandles): void {
  applyPopupDimensions(handles.frameWrap, window, handles.layout);
  positionHandle(handles.resizeHandle, handles.layout.position);
  setHandleVisible(handles.resizeHandle, handles.resizable && !isMobileViewport(window));
}

/**
 * FEATURE-34 — re-paint the launcher button when the host calls
 * `updateTheme(...)` post-init. Preserves a host-set custom icon + accent
 * color (theme change must not clobber explicit host branding). We do NOT
 * touch `iframe.src` — the panel gets the new theme via the `update-theme`
 * postMessage envelope (zero re-render, no chat state lost).
 */
export function applyTheme(handles: DomHandles, theme: ThemeName): void {
  paintButton(handles.button, theme, handles.accentColor, handles.button.ownerDocument);
  setButtonIcon(handles.button, theme, handles.icon, handles.layout.launcherSize);
  handles.button.dataset.theme = theme;
  handles.iframe.dataset.theme = theme;
}

export function destroyWidget(handles: DomHandles | null): void {
  if (handles === null) return;
  handles.root.remove();
}
